import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type AdminStayStatus =
  | 'PREPARING'
  | 'READY'
  | 'CHECKED_IN'
  | 'CHECKED_OUT'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'TERMINATED';
export type AdminStaySortBy = 'created_at' | 'start_at' | 'end_at' | 'status' | 'stay_id';
export type AdminStaySortDirection = 'asc' | 'desc';
const STAY_SORT_FIELDS: readonly AdminStaySortBy[] = [
  'created_at',
  'start_at',
  'end_at',
  'status',
  'stay_id',
];
const isStaySortDirection = (value: unknown): value is AdminStaySortDirection =>
  value === 'asc' || value === 'desc';
const STAY_STATUSES: readonly AdminStayStatus[] = [
  'PREPARING',
  'READY',
  'CHECKED_IN',
  'CHECKED_OUT',
  'CANCELLED',
  'NO_SHOW',
  'TERMINATED',
];

export interface AdminStayListItem {
  stay_id: string;
  property_id: string;
  status: string;
  start_at: string;
  end_at: string;
  created_at: string;
  updated_at: string;
}
export interface AdminStayFilters {
  cursor?: string | null;
  sortBy?: AdminStaySortBy;
  sortDirection?: AdminStaySortDirection;
  propertyId?: string;
  status?: AdminStayStatus;
  startsAt?: string;
  endsAt?: string;
}
export interface AdminStayPage {
  items: AdminStayListItem[];
  nextCursor: string | null;
}

export interface AdminStaySource {
  source_id: string;
  status: string;
  start_at: string;
  end_at: string;
}
export interface AdminStay {
  stay_id: string;
  status: string;
  start_at: string;
  end_at: string;
  sources: AdminStaySource[];
}
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const exact = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '투숙 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });

function parseStayListItem(value: unknown): AdminStayListItem {
  if (
    !record(value) ||
    !exact(value, [
      'stay_id',
      'property_id',
      'status',
      'start_at',
      'end_at',
      'created_at',
      'updated_at',
    ]) ||
    !isUuid(value.stay_id) ||
    value.stay_id !== value.stay_id.toLowerCase() ||
    !isUuid(value.property_id) ||
    value.property_id !== value.property_id.toLowerCase() ||
    !text(value.status) ||
    !isOffsetDateTime(value.start_at) ||
    !isOffsetDateTime(value.end_at) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at) ||
    Date.parse(value.start_at) >= Date.parse(value.end_at)
  )
    throw invalid();
  return value as unknown as AdminStayListItem;
}

function validateStayFilters(filters: AdminStayFilters): void {
  if (
    (filters.cursor != null &&
      (!text(filters.cursor) ||
        filters.cursor.length < 1 ||
        filters.cursor.length > (filters.sortBy === undefined ? 256 : 1024))) ||
    (filters.sortBy === undefined) !== (filters.sortDirection === undefined) ||
    (filters.sortBy !== undefined && !STAY_SORT_FIELDS.includes(filters.sortBy)) ||
    (filters.sortDirection !== undefined && !isStaySortDirection(filters.sortDirection)) ||
    (filters.propertyId !== undefined &&
      (!isUuid(filters.propertyId) || filters.propertyId !== filters.propertyId.toLowerCase())) ||
    (filters.status !== undefined && !STAY_STATUSES.includes(filters.status)) ||
    (filters.startsAt === undefined) !== (filters.endsAt === undefined) ||
    (filters.startsAt !== undefined && !isOffsetDateTime(filters.startsAt)) ||
    (filters.endsAt !== undefined && !isOffsetDateTime(filters.endsAt)) ||
    (filters.startsAt !== undefined &&
      filters.endsAt !== undefined &&
      Date.parse(filters.startsAt) >= Date.parse(filters.endsAt))
  )
    throw invalid();
}

/** Read the current stored Stay page. The API applies a half-open Stay-period overlap filter. */
export async function readAdminStays(
  client: ServiceClient,
  filters: AdminStayFilters = {},
  signal?: AbortSignal,
): Promise<AdminStayPage> {
  validateStayFilters(filters);
  const value = await client.get<unknown>('admin/stays', {
    params: {
      limit: 20,
      ...(filters.cursor ? { cursor: filters.cursor } : {}),
      ...(filters.sortBy ? { sort_by: filters.sortBy } : {}),
      ...(filters.sortDirection ? { sort_direction: filters.sortDirection } : {}),
      ...(filters.propertyId ? { property_id: filters.propertyId } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.startsAt ? { starts_at: filters.startsAt } : {}),
      ...(filters.endsAt ? { ends_at: filters.endsAt } : {}),
    },
    signal,
  });
  if (
    !record(value) ||
    !exact(value, ['items', 'next_cursor']) ||
    !Array.isArray(value.items) ||
    value.items.length > 20 ||
    !(
      value.next_cursor === null ||
      (text(value.next_cursor) &&
        value.next_cursor.length <= (filters.sortBy === undefined ? 256 : 1024))
    )
  )
    throw invalid();
  return { items: value.items.map(parseStayListItem), nextCursor: value.next_cursor };
}

function parseSource(value: unknown): AdminStaySource {
  if (
    !record(value) ||
    !exact(value, ['source_id', 'status', 'start_at', 'end_at']) ||
    !isUuid(value.source_id) ||
    !text(value.status) ||
    !isOffsetDateTime(value.start_at) ||
    !isOffsetDateTime(value.end_at)
  )
    throw invalid();
  return {
    source_id: value.source_id,
    status: value.status,
    start_at: value.start_at,
    end_at: value.end_at,
  };
}

function parseStay(value: unknown, expectedStayId: string): AdminStay {
  if (
    !record(value) ||
    !exact(value, ['stay_id', 'status', 'start_at', 'end_at', 'sources']) ||
    value.stay_id !== expectedStayId ||
    !text(value.status) ||
    !isOffsetDateTime(value.start_at) ||
    !isOffsetDateTime(value.end_at) ||
    !Array.isArray(value.sources)
  )
    throw invalid();
  return {
    stay_id: value.stay_id,
    status: value.status,
    start_at: value.start_at,
    end_at: value.end_at,
    sources: value.sources.map(parseSource),
  };
}

/** Read current stored Stay root facts and Booking source facts through the admin endpoint. */
export async function readAdminStay(
  client: ServiceClient,
  stayId: string,
  signal?: AbortSignal,
): Promise<AdminStay> {
  if (!isUuid(stayId) || stayId !== stayId.toLowerCase()) throw invalid();
  const value = await client.get<unknown>(`admin/stays/${stayId}`, { signal });
  return parseStay(value, stayId);
}
